import React, { useContext, useEffect, useMemo, useState, FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// MUI
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip, { ChipProps } from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// MUI Icons
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// Lightbox
import Lightbox, { SlideImage } from 'yet-another-react-lightbox';
import { Download, Zoom } from 'yet-another-react-lightbox/plugins';
import 'yet-another-react-lightbox/styles.css';

// Custom Components
import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import { saveFile } from '@/lib/fileControlUtility';

// Models
import { upitnikOdgovorStatusItems, UpitnikOdgovorStatus } from '@/models/groblja/upitnik_odgovori';

// Services
import { upitnikService } from '@/services/upitnikService';

// Types
import { DCFile, DCRecord } from '@/@types/lib/dataController';

const OZNAKA_PARTS = ['odjel', 'polje', 'red', 'broj'] as const;
const TILE_SIZE = 96;

const formatDateTime = (value: unknown) => (value ? moment(value as string).format('DD.MM.YYYY. HH:mm') : null);

const Row = ({ label, children }: { label: string; children: ReactNode }) =>
  children === null || children === undefined || children === '' ? null : (
    <>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </>
  );

const UpitnikOdgovorDialog: FunctionComponent<IDialogProps> = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { hideDialog } = useContext(DialogContext);

  const { record } = props as { record: DCRecord };
  const brojPrivitaka = Number(record.broj_privitaka) || 0;

  const [privitci, setPrivitci] = useState<DCFile[] | null>(brojPrivitaka === 0 ? [] : null);
  const [privitciFailed, setPrivitciFailed] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (brojPrivitaka === 0) return;
    upitnikService
      .getOdgovorPrivitci(record.id as number)
      .then((resp) => {
        if (!resp.success) throw new Error('getOdgovorPrivitci');
        setPrivitci(Array.isArray(resp.data) ? (resp.data as DCFile[]) : []);
      })
      .catch(() => setPrivitciFailed(true));
  }, [record.id]);

  const slike = useMemo(() => (privitci ?? []).filter((f) => f.type?.startsWith('image/')), [privitci]);
  const slides: SlideImage[] = slike.map((f) => ({ src: `data:${f.type};base64,${f.content}`, downloadFilename: f.name }));

  const statusIndex = upitnikOdgovorStatusItems.values.indexOf(record.status as UpitnikOdgovorStatus);
  const adresa = [record.ulica, [record.ptt, record.mjesto].filter(Boolean).join(' '), record.drzava].filter(Boolean).join('\n');

  const renderPrivitci = () => {
    if (privitciFailed) return <Alert severity="error">{t('groblja:upitnik_odgovori.privitci_greska')}</Alert>;
    if (privitci === null) {
      return Array.from({ length: brojPrivitaka }, (_, i) => (
        <Skeleton key={i} variant="rounded" animation="wave" width={TILE_SIZE} height={TILE_SIZE} />
      ));
    }
    if (privitci.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('groblja:upitnik_odgovori.privitci_nema')}
        </Typography>
      );
    }
    return privitci.map((f) => {
      const slikaIndex = slike.indexOf(f);
      return (
        <ButtonBase
          key={f.id}
          title={f.name}
          onClick={() => (slikaIndex >= 0 ? setLightboxIndex(slikaIndex) : saveFile(f))}
          sx={{ width: TILE_SIZE, height: TILE_SIZE, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}
        >
          {slikaIndex >= 0 ? (
            <Box component="img" src={slides[slikaIndex].src} alt={f.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, px: 0.5 }}>
              <PictureAsPdfIcon color="error" />
              <Typography variant="caption" noWrap sx={{ maxWidth: TILE_SIZE - 8 }}>
                {f.name}
              </Typography>
            </Box>
          )}
        </ButtonBase>
      );
    });
  };

  return (
    <DraggableDialog open maxWidth="sm" minHeight="auto" fullScreen={fullScreen} onClose={() => hideDialog()}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('groblja:upitnik_odgovori.detalji_naslov')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={() => hideDialog()} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {statusIndex >= 0 ? (
            <Chip
              size="small"
              label={t(upitnikOdgovorStatusItems.labels[statusIndex])}
              color={upitnikOdgovorStatusItems.chipColors[statusIndex] as ChipProps['color']}
            />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {`${t('groblja:upitnik_odgovori.created_on')}: ${formatDateTime(record.created_on)}`}
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {t('groblja:upitnik_odgovori.grobno_mjesto')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, mb: 2 }}>
          <Row label={t('groblja:upitnici.groblje')}>{record.groblje_naziv as string}</Row>
          {OZNAKA_PARTS.map((p) => (
            <Row key={p} label={t(`groblja:upitnik.${p}`)}>
              {record[p] as string}
            </Row>
          ))}
          <Row label={t('groblja:upitnici.ima_obveznika')}>{t(record.ima_obveznika ? 'common.yes' : 'common.no')}</Row>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, mb: 2 }}>
          <Row label={t('groblja:upitnik.ime')}>{record.ime as string}</Row>
          <Row label={t('groblja:upitnik.prezime')}>{record.prezime as string}</Row>
          <Row label={t('groblja:upitnik.oib')}>{record.oib as string}</Row>
          <Row label={t('groblja:upitnik.kontakt')}>{record.kontakt as string}</Row>
          <Row label={t('groblja:upitnik_odgovori.adresa')}>{adresa}</Row>
          <Row label={t('groblja:upitnik.napomena')}>{record.napomena as string}</Row>
          <Row label={t('groblja:upitnik_odgovori.obradio')}>{record.obradio as string}</Row>
          <Row label={t('groblja:upitnik_odgovori.obradjeno_on')}>{formatDateTime(record.obradjeno_on)}</Row>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('groblja:upitnik.privitci')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{renderPrivitci()}</Box>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonClose variant="outlined" onClick={() => hideDialog()} />
      </DialogActions>
      <Lightbox
        open={lightboxIndex !== null}
        close={() => setLightboxIndex(null)}
        slides={slides}
        index={lightboxIndex ?? 0}
        carousel={{ finite: true }}
        plugins={[Zoom, Download]}
      />
    </DraggableDialog>
  );
};

export default UpitnikOdgovorDialog;
